import { useState, useEffect, useCallback, useRef } from "react";
import { apiGet } from "../lib/apiClient.js";
import { makeTtlCache } from "../lib/forecast/reportCache.js";
import {
  findEntryByMoment,
  isReturnToLatestMoment,
  asyncEntrySnapshot,
  deriveScrubView,
  holdSnapshotThroughLoad,
} from "../lib/forecast/reforecastEntries.js";
import { fetchForecastReport, preloadForecastReportsFull } from "../lib/forecast/reportPointer.js";

// Per-tab TTL cache for scrubbed async reports, keyed by row_key. Module
// singleton so scrubbing back to a fire is instant across open→close→reopen. Same
// key space + TTL as the plot-series cache but a distinct Map (a scrubbed report
// and a plotted one may each fetch once — a single duplicate not worth coupling
// the two hook modules over).
const REPORT_TTL_MS = 5 * 60 * 1000;
const _scrubReportCache = makeTtlCache({ ttlMs: REPORT_TTL_MS });

/**
 * One version entry's snapshot, through the same cache the scrub warms: inline for a manual
 * entry, fetched for an async fire. Resolves null when it can't be read; never rejects.
 * @param {object|null} entry a buildReforecastEntries entry
 * @returns {Promise<object|null>}
 */
export function loadEntrySnapshot(entry, { apiGetImpl = apiGet, cache = _scrubReportCache } = {}) {
  if (!entry) return Promise.resolve(null);
  if (entry.kind !== "async") return Promise.resolve(entry.snapshot || null);
  if (!entry.rowKey) return Promise.resolve(null);
  // `fetchForecastReport` folds a `!ok` answer to the same null as "nothing to scrub to", and
  // `apiGet` never throws, so the answer is observed on the way through: a report the server
  // refused is a lost snapshot, warned like a rejection. A cache hit makes no request.
  let answer;
  const observed = async (...args) => {
    answer = await apiGetImpl(...args);
    return answer;
  };
  return fetchForecastReport(entry.rowKey, { apiGet: observed, cache })
    .then((src) => {
      if (answer && !answer.ok) {
        console.warn(
          "reforecast_scrub.snapshot_failed",
          new Error(`GET /api/forecast/report answered status ${answer.status}`),
        );
      }
      return src ? asyncEntrySnapshot(entry, src) : null;
    })
    .catch((err) => {
      console.warn("reforecast_scrub.snapshot_failed", err);
      return null;
    });
}

/**
 * Snapshot-scrub state for the unified reforecast list — the generalization of
 * App.jsx's old manual-only `viewMomentIso`/`viewSnap`/`selectSnapshotByMoment`.
 * A selected MANUAL entry resolves to its inline snapshot instantly; a selected
 * ASYNC entry lazily fetches its report (shared read-bridge + TTL cache), mapped
 * into the same snapshot shape (`asyncEntrySnapshot`), so everything downstream of
 * `viewSnap` (timeline rewind, gold guide, line truncation, report modal) is
 * unchanged — manual and automatic scrub through one path.
 *
 * @param {{
 *   entries: Array,           // buildReforecastEntries output (the scrub list)
 *   activeId: string|null,    // active forecast id — resets the scrub on switch
 *   forecastTracking: boolean,// FT gate — resets the scrub when turned off
 *   apiGetImpl?: Function,    // injectable for tests (preload + per-scrub report GET)
 *   cache?: object            // injectable TTL cache
 * }} args
 * @returns {{
 *   viewMomentIso: string|null,   // the scrubbed moment, or null when live
 *   viewEntry: object|null,       // the selected entry (kind/isResolution/status) — null when live
 *   viewSnap: object|null,        // resolved snapshot (manual inline | async fetched | null)
 *   scrubbing: boolean,           // an async report fetch is in flight
 *   scrubToMoment: (iso:string)=>void, // select an entry by its moment (live → returns to latest)
 *   returnToLive: ()=>void,       // clear the scrub
 * }}
 */
export function useReforecastScrub({
  entries,
  activeId,
  forecastTracking,
  apiGetImpl = apiGet,
  cache = _scrubReportCache,
}) {
  const [viewMomentIso, setViewMomentIso] = useState(null);
  // Resolved ASYNC snapshot keyed by the async entry's `rowKey` (globally unique
  // per contract — see the fetch effect), so a resolve for a superseded scrub
  // target never leaks into the current view (same keyed-state discipline as
  // useReportPointerSeries). `snap: null` at a matching key is a settled miss
  // (couldn't load) — distinct from key-mismatch (still fetching).
  const [asyncState, setAsyncState] = useState({ key: null, snap: null });

  // Reset the scrub when the active forecast changes (log switch / reload) or when
  // Forecast Tracking is turned off — mirrors the prior inline effects so the
  // marks + "return to latest" affordance can't strand the user in a past view.
  useEffect(() => {
    setViewMomentIso(null);
  }, [activeId]);
  useEffect(() => {
    if (!forecastTracking) setViewMomentIso(null);
  }, [forecastTracking]);

  // PRELOAD every async fire's report into the scrub cache as soon as the entry set
  // is known. Without a warm cache the per-scrub fetch below is the FIRST read of
  // each report (~1–2s cold), so scrubbing faster than a report loads left the
  // report modal's body frozen on the last-settled snapshot (holdSnapshotThroughLoad)
  // while only the header / date / "i of n" pill advanced — "the same content for
  // every scrub position". A warm cache makes each per-scrub read a microtask-fast
  // hit, so the body tracks the position even under rapid scrubbing (the flash-fix
  // hold-window closes within a frame instead of stalling on a cold read).
  //
  // Warming goes through the SINGLE-KEY route (preloadForecastReportsFull), NOT the
  // batched `POST /forecast/reports`. That route deliberately omits the structured
  // `report` block, and because fetchForecastReport short-circuits on ANY cache hit,
  // a batch-primed (lean) entry actively PREVENTS the single-key read that would
  // have returned it — so async fires fell back to the legacy parsed narrative even
  // when BigTable held a full structured report. Preloading (rather than fetching on
  // selection) also means the structured grid is already there when the user lands,
  // instead of "flashing in" a beat after the narrative painted.
  //
  // Keys are newest-fire-FIRST: `entries` is time-ascending (buildReforecastEntries),
  // so reversing puts the most likely scrub targets at the head of the bounded
  // preload. preloadForecastReportsFull is best-effort (never throws), fetches only
  // cache misses, and is capped + concurrency-limited, so a re-run on an entry-set
  // change is cheap and an hourly-cadence contract can't fan out hundreds of reads.
  // Keys are derived INSIDE the effect and the injectable seams are honest deps (no
  // exhaustive-deps suppression); `entries` is memoized by the caller, so this
  // re-runs on a real fire-set change, not every render. Gated on forecastTracking so
  // a preload can't START while the feature is off, independent of what entries the
  // caller happens to pass — but note that is a start-gate, NOT cancellation: the
  // preload is fire-and-forget, so turning Forecast Tracking off mid-flight re-runs
  // this effect (returning early) while the already-launched worker pool finishes its
  // remaining reads. That is deliberate — the preload is bounded (≤ the cap),
  // read-only, and idempotent, so letting a few reads land costs a warm cache entry
  // and nothing else; threading an AbortController through it would add cancellation
  // machinery to this hook's load-bearing keyed-state/cleanup discipline to save
  // at most a handful of point-reads.
  useEffect(() => {
    if (!forecastTracking) return;
    const rowKeys = (Array.isArray(entries) ? entries : [])
      .filter((e) => e && e.kind === "async" && typeof e.rowKey === "string" && e.rowKey.length > 0)
      .map((e) => e.rowKey)
      .reverse();
    if (!rowKeys.length) return;
    // Fire-and-forget: the preload warms the cache in the background; the per-scrub
    // read below awaits its own fetch (a hit once warmed). `void` marks the
    // intentionally-unawaited promise (repo convention — cf. useRealtimeChannel.js).
    void preloadForecastReportsFull(rowKeys, { apiGet: apiGetImpl, cache });
  }, [entries, forecastTracking, apiGetImpl, cache]);

  const entry = viewMomentIso ? findEntryByMoment(entries, viewMomentIso) : null;
  const entryKind = entry ? entry.kind : null;
  const entryRowKey = entry ? entry.rowKey : null;

  // The resolved-async cache is keyed by the entry's `rowKey`, NOT by
  // `viewMomentIso`: panel fires share a `fire_ts` (→ moment string) across
  // contracts, but `rowKey` (`panel_<model>#<ts>#<qkey>`) is globally unique per
  // contract, so it's the correct identity. Same rowKey ⇒ genuinely the same
  // report (safe to reuse); different rowKey ⇒ a fresh fetch. This also closes the
  // race where an in-flight fetch resolves AFTER a forecast switch: its result is
  // stamped under the OLD rowKey, which can never match the new forecast's entry.
  useEffect(() => {
    if (entryKind !== "async" || !entryRowKey) return undefined;
    // Settled-for-fetch = this rowKey is stamped AND that stamp is a definitive
    // result (a snapshot or a stable 404). A `transient` stamp is NOT settled: we
    // want to re-fetch it — but only on a fresh effect run (a re-scrub changes the
    // rowKey dep), never in a loop (deps are unchanged after we stamp it).
    if (asyncState.key === entryRowKey && !asyncState.transient) return undefined;
    let cancelled = false;
    fetchForecastReport(entryRowKey, { apiGet: apiGetImpl, cache })
      .then((src) => {
        if (cancelled) return;
        // Three settled outcomes, all stamped so the loading state ends:
        //   - snapshot → resolved.
        //   - null AND cache holds null → STABLE 404 miss (fetchForecastReport
        //     cached it) → permanent miss, never re-fetched.
        //   - null AND cache miss → TRANSIENT failure (network/5xx; not cached) →
        //     stamp `transient:true` so the pill shows "couldn't load" (not a
        //     perpetual "loading…"), but the guard above still allows a retry on
        //     the next scrub to this fire.
        if (src) setAsyncState({ key: entryRowKey, snap: asyncEntrySnapshot(entry, src) });
        else if (cache.get(entryRowKey) === null) setAsyncState({ key: entryRowKey, snap: null });
        else setAsyncState({ key: entryRowKey, snap: null, transient: true });
      })
      .catch(() => {
        if (!cancelled) setAsyncState({ key: entryRowKey, snap: null, transient: true });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryRowKey, entryKind]);

  // Derive the resolved snapshot via the pure resolver (unit-tested): manual →
  // inline; async → resolved once the rowKey-keyed cache matches (null there =
  // settled miss → caller keeps live), still-fetching → scrubbing.
  const { viewSnap: resolvedSnap, scrubbing } = deriveScrubView(entry, asyncState);

  // Keep-previous-while-loading (pure holdSnapshotThroughLoad): during an async
  // fetch window resolvedSnap is null and callers fall back to LIVE, so scrubbing
  // A→B flashed "latest" for the settle. Hold the last resolved snapshot through
  // the load instead (A → hold A → B). The `usePrevious` pattern: READ the ref
  // during render (it reflects the last COMMITTED render), compute the display,
  // and WRITE the carry-forward value in a post-commit effect — never during
  // render, so a discarded concurrent/StrictMode render can't leave the ref out
  // of sync with the committed UI. No re-render (a ref write is invisible).
  const lastSnapRef = useRef(null);
  const { display: viewSnap, next } = holdSnapshotThroughLoad(lastSnapRef.current, {
    selected: !!viewMomentIso,
    scrubbing,
    resolvedSnap,
  });
  useEffect(() => {
    lastSnapRef.current = next;
  }, [next]);

  const scrubToMoment = useCallback(
    (iso) => {
      // Selecting the TAIL (the at-rest/latest position — newest resolution when
      // resolved, else the live forecast sentinel) returns to live (null); any
      // earlier entry is a real rewind target. Keying off the tail (not the
      // `isLive` flag) keeps the live forecast sentinel selectable now that
      // resolution entries follow it.
      setViewMomentIso(isReturnToLatestMoment(entries, iso) ? null : iso);
    },
    [entries],
  );
  const returnToLive = useCallback(() => setViewMomentIso(null), []);

  // Expose the selected entry so callers can render by KIND (a forecast vintage
  // vs a distinct resolution event) — this is what supersedes the old
  // `effectiveVerdictStatus`/`viewingSnapshot` override: a verdict shows only when
  // the selected entry is a resolution (or at rest), never on a forecast vintage.
  return { viewMomentIso, viewEntry: entry, viewSnap, scrubbing, scrubToMoment, returnToLive };
}
